import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { t } from '../../i18n/nl.js';
import { getPushPreferences, savePushPreferences } from '../../utils/pushPreferences.js';
import { isoDateToDutch } from '../../utils/dateOfBirth.js';
import { uploadTrainingImage } from '../../utils/trainingImages.js';

const CLIENT_PUSH_CATEGORIES = ['lessons_schedule', 'pt_appointments', 'billing', 'agreements', 'coach_messages'];
const PUSH_CAT_I18N_KEY = {
  lessons_schedule: 'lessonsSchedule',
  pt_appointments: 'ptAppointments',
  billing: 'billing',
  agreements: 'agreements',
  coach_messages: 'coachMessages',
};

export default function ClientInstellingenPage() {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    customer_number: '',
    full_name: '',
    email: '',
    date_of_birth: '',
    phone: '',
    street: '',
    house_number: '',
    postal_code: '',
    city: '',
  });
  const [pushPrefs, setPushPrefs] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Agenda-sync state
  const [calToken, setCalToken] = useState(null);
  const [calLoading, setCalLoading] = useState(false);
  const [calCopied, setCalCopied] = useState(false);
  const [showRegenConfirm, setShowRegenConfirm] = useState(false);

  const calFeedUrl = calToken
    ? `https://rvkigbljdpkiqzttedwu.supabase.co/functions/v1/client-calendar-feed?token=${calToken}`
    : null;

  useEffect(() => {
    if (!user?.id) return;
    supabase.rpc('get_calendar_feed_token').then(({ data }) => {
      if (data) setCalToken(data);
    });
  }, [user?.id]);

  async function handleGenerateCalToken() {
    setCalLoading(true);
    try {
      const { data, error: err } = await supabase.rpc('generate_calendar_feed_token');
      if (err) throw err;
      setCalToken(data);
      setShowRegenConfirm(false);
      showSuccess(t('agendaSync.copied').replace('✓ ', ''));
    } catch (e) {
      showError(t('agendaSync.foutGenereren') + e.message);
    } finally {
      setCalLoading(false);
    }
  }

  async function handleCopyCalUrl() {
    try {
      await navigator.clipboard.writeText(calFeedUrl);
      setCalCopied(true);
      setTimeout(() => setCalCopied(false), 2000);
    } catch {
      showError(t('agendaSync.foutKopieren'));
    }
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      showError(t('wachtwoordWijzigen.foutTeKort'));
      return;
    }
    if (newPassword !== confirmPassword) {
      showError(t('wachtwoordWijzigen.foutMismatch'));
      return;
    }
    setChangingPassword(true);
    try {
      const { error: err } = await supabase.auth.updateUser({ password: newPassword });
      if (err) throw err;
      setNewPassword('');
      setConfirmPassword('');
      showSuccess(t('wachtwoordWijzigen.succes'));
    } catch (err) {
      showError(t('wachtwoordWijzigen.fout') + err.message);
    } finally {
      setChangingPassword(false);
    }
  }

  useEffect(() => {
    if (!user?.id) return;
    getPushPreferences(supabase, user.id).then(setPushPrefs).catch(() => {});
  }, [user?.id]);

  async function handleTogglePushCat(key, checked) {
    const previous = pushPrefs;
    const next = { ...pushPrefs, [key]: checked };
    setPushPrefs(next);
    try {
      await savePushPreferences(supabase, user.id, next);
    } catch (e) {
      setPushPrefs(previous);
      showError(t('pushCat.foutOpslaan') + e.message);
    }
  }

  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('profiles')
      .select('customer_number, full_name, email, date_of_birth, phone, street, house_number, postal_code, city, avatar_url')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data, error: err }) => {
        if (err) { setError(t('clientInstellingen.foutLaden') + err.message); return; }
        if (data) {
          setAvatarUrl(data.avatar_url || '');
          setForm({
          customer_number: data.customer_number || '',
          full_name: data.full_name || '',
          email: data.email || '',
          date_of_birth: isoDateToDutch(data.date_of_birth),
          phone: data.phone || '',
          street: data.street || '',
          house_number: data.house_number || '',
          postal_code: data.postal_code || '',
          city: data.city || '',
          });
        }
        setLoading(false);
      });
  }, [user?.id]);

  async function handleAvatarChange(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setAvatarUploading(true);
    try {
      const url = await uploadTrainingImage(supabase, 'profiles', user.id, file);
      const { data: updRow, error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: url })
        .eq('id', user.id)
        .select('id')
        .maybeSingle();
      if (updateError) throw updateError;
      if (!updRow) { showError(t('common.wijzigingNietOpgeslagen')); return; }
      setAvatarUrl(url);
      showSuccess(t('clientInstellingen.profielfotoOpgeslagen'));
    } catch (uploadError) {
      const key = uploadError?.message === 'image_too_large'
        ? 'clientInstellingen.profielfotoTeGroot'
        : 'clientInstellingen.profielfotoFout';
      showError(t(key));
    } finally {
      setAvatarUploading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const { data: updRow, error: updErr } = await supabase
        .from('profiles')
        .update({
          full_name: form.full_name,
          phone: form.phone || null,
          street: form.street || null,
          house_number: form.house_number || null,
          postal_code: form.postal_code || null,
          city: form.city || null,
        })
        .eq('id', user.id)
        .select('id')
        .maybeSingle();
      if (updErr) throw updErr;
      if (!updRow) { setError(t('common.wijzigingNietOpgeslagen')); return; }
      showSuccess(t('clientInstellingen.opslaanSucces'));
    } catch (err) {
      setError(t('clientInstellingen.opslaanFout') + err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="loading-center" style={{ height: '60vh' }}><div className="spinner" /></div>;

  return (
    <div style={{ maxWidth: 600 }}>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('clientInstellingen.title')}</h1>
          <p className="page-subtitle">{t('clientInstellingen.subtitle')}</p>
        </div>
      </div>

      <div className="card">
        {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">{t('clientInstellingen.labelProfielfoto')}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {avatarUrl ? (
                <img src={avatarUrl} alt={t('clientInstellingen.profielfotoAlt')} style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '1px solid var(--color-border)' }} />
              ) : (
                <div aria-hidden="true" style={{ width: 80, height: 80, borderRadius: '50%', display: 'grid', placeItems: 'center', background: 'rgba(29,158,117,0.12)', color: 'var(--color-primary)', fontSize: 28, fontWeight: 700 }}>
                  {(form.full_name || user?.email || '?').trim().slice(0, 1).toUpperCase()}
                </div>
              )}
              <div>
                <label className="btn" style={{ cursor: avatarUploading ? 'wait' : 'pointer', display: 'inline-flex' }}>
                  {avatarUploading ? t('clientInstellingen.profielfotoBezig') : t('clientInstellingen.profielfotoKiezen')}
                  <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleAvatarChange} disabled={avatarUploading} style={{ display: 'none' }} />
                </label>
                <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--color-text-muted)' }}>{t('clientInstellingen.profielfotoHint')}</p>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">{t('clientInstellingen.labelKlantnummer')}</label>
            <input
              className="form-input"
              value={form.customer_number}
              readOnly
              style={{ background: 'var(--color-bg)', color: 'var(--color-text-muted)', cursor: 'default' }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('clientInstellingen.labelVolledigeNaam')}</label>
            <input
              className="form-input"
              value={form.full_name}
              onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('clientInstellingen.labelEmail')}</label>
            <input
              className="form-input"
              value={form.email}
              readOnly
              style={{ background: 'var(--color-bg)', color: 'var(--color-text-muted)', cursor: 'default' }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('clientInstellingen.labelGeboortedatum')}</label>
            <input
              className="form-input"
              value={form.date_of_birth || t('clientInstellingen.geboortedatumNietIngevuld')}
              readOnly
              style={{ background: 'var(--color-bg)', color: 'var(--color-text-muted)', cursor: 'default' }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('clientInstellingen.labelTelefoon')}</label>
            <input
              className="form-input"
              value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('clientInstellingen.labelStraat')}</label>
              <input
                className="form-input"
                value={form.street}
                onChange={e => setForm(f => ({ ...f, street: e.target.value }))}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('clientInstellingen.labelHuisnummer')}</label>
              <input
                className="form-input"
                value={form.house_number}
                onChange={e => setForm(f => ({ ...f, house_number: e.target.value }))}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginTop: 12 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('clientInstellingen.labelPostcode')}</label>
              <input
                className="form-input"
                value={form.postal_code}
                onChange={e => setForm(f => ({ ...f, postal_code: e.target.value }))}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('clientInstellingen.labelWoonplaats')}</label>
              <input
                className="form-input"
                value={form.city}
                onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
              />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? '…' : t('clientInstellingen.opslaan')}
            </button>
          </div>
        </form>
      </div>

      <div className="card" style={{ marginTop: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
          {t('wachtwoordWijzigen.title')}
        </h2>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, marginBottom: 20, lineHeight: 1.5 }}>
          {t('wachtwoordWijzigen.intro')}
        </p>
        <form onSubmit={handleChangePassword}>
          <div className="form-group">
            <label className="form-label">{t('wachtwoordWijzigen.labelNieuw')}</label>
            <input
              className="form-input"
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder={t('wachtwoordWijzigen.placeholderNieuw')}
              autoComplete="new-password"
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('wachtwoordWijzigen.labelHerhaal')}</label>
            <input
              className="form-input"
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" className="btn btn-primary" disabled={changingPassword}>
              {changingPassword ? t('auth.loading') : t('wachtwoordWijzigen.opslaanKnop')}
            </button>
          </div>
        </form>
      </div>

      <div className="card" style={{ marginTop: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
          {t('pushCat.title')}
        </h2>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, marginBottom: 20, lineHeight: 1.5 }}>
          {t('pushCat.introClient')}
        </p>

        {pushPrefs && CLIENT_PUSH_CATEGORIES.map(key => {
          const i18nKey = PUSH_CAT_I18N_KEY[key];
          return (
            <div
              key={key}
              style={{ padding: '12px 16px', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: 16 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <input
                  type="checkbox"
                  id={`pushcat_${key}`}
                  checked={pushPrefs[key] ?? true}
                  onChange={e => handleTogglePushCat(key, e.target.checked)}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <label htmlFor={`pushcat_${key}`} className="form-label" style={{ marginBottom: 0, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                  {t(`pushCat.${i18nKey}Label`)}
                </label>
              </div>
              <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginLeft: 30, marginBottom: 0, lineHeight: 1.5 }}>
                {t(`pushCat.${i18nKey}Desc`)}
              </p>
            </div>
          );
        })}

        <p style={{ fontSize: 12, color: 'var(--color-text-muted)', fontStyle: 'italic', margin: 0 }}>
          {t('pushCat.alwaysOn')}
        </p>
      </div>

      <div className="card" style={{ marginTop: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
          {t('agendaSync.title')}
        </h2>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, marginBottom: 20, lineHeight: 1.5 }}>
          {t('agendaSync.intro')}
        </p>

        {calToken ? (
          <>
            <label className="form-label">{t('agendaSync.urlLabel')}</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input
                className="form-input"
                value={calFeedUrl}
                readOnly
                style={{ flex: 1, fontSize: 12, background: 'var(--color-bg)', cursor: 'text' }}
                onClick={e => e.target.select()}
              />
              <button type="button" className="btn btn-primary" onClick={handleCopyCalUrl} style={{ whiteSpace: 'nowrap' }}>
                {calCopied ? t('agendaSync.copied') : t('agendaSync.copyBtn')}
              </button>
            </div>

            <details style={{ marginBottom: 16 }}>
              <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)' }}>
                {t('agendaSync.instructionTitle')}
              </summary>
              <ol style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.8, paddingLeft: 20, marginTop: 8 }}>
                <li>{t('agendaSync.step1')}</li>
                <li>{t('agendaSync.step2')}</li>
                <li>{t('agendaSync.step3')}</li>
                <li>{t('agendaSync.step4')}</li>
              </ol>
            </details>

            {showRegenConfirm ? (
              <div style={{ padding: 12, background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginTop: 8 }}>
                <p style={{ fontSize: 13, marginBottom: 12 }}>{t('agendaSync.regenerateWarning')}</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="btn btn-primary" onClick={handleGenerateCalToken} disabled={calLoading}>
                    {calLoading ? t('agendaSync.generating') : t('agendaSync.regenerateConfirm')}
                  </button>
                  <button type="button" className="btn" onClick={() => setShowRegenConfirm(false)}>
                    {t('agendaSync.regenerateCancel')}
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" className="btn" onClick={() => setShowRegenConfirm(true)} style={{ fontSize: 13 }}>
                {t('agendaSync.regenerateBtn')}
              </button>
            )}
          </>
        ) : (
          <button type="button" className="btn btn-primary" onClick={handleGenerateCalToken} disabled={calLoading}>
            {calLoading ? t('agendaSync.generating') : t('agendaSync.generateBtn')}
          </button>
        )}
      </div>
    </div>
  );
}
